//-- Copyright (c) 2021 TMT Insights Group, LLC
import { useContext, useState } from 'react';
import styled from '@emotion/styled';
import { Button, FormControl, IconButton, InputLabel, Menu, MenuItem, Select } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { applyFilters } from '@/redux/slices/dataTableSlice';
import { deleteSavedSearch, undoDeleteSavedSearch } from '@/redux/slices/authSlice';
import { selectSavedSearches } from '@/redux/selectors/authSelectors';
import { useToast } from '@/components/_common/Toast';
import PolarisIcon from '@/components/_common/PolarisIcon';
import SavedSearchEdit from '@/components/_common/Table/SavedSearchEdit';
import { cleanSavedFilters } from '@/components/_common/Filters/FiltersQuerystringController';

export default function SavedSearches({ isOnDrawer = false }) {
  const isFiltersV2 = useContext(ConfigContext)?.filtersVersion === 2;
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [editingSearch, setEditingSearch] = useState(null);
  const savedSearches = useAppSelector(selectSavedSearches);
  const { success, closeSnackbar } = useToast();

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const onSavedSearchClick = (e) => {
    const savedData = savedSearches[e.target.id] || savedSearches[e.target.value];
    dispatch(applyFilters({ ...savedData, filters: cleanSavedFilters(savedData.filters, false) }));
    handleClose();
  };

  const onEdit = (e, name) => {
    e.stopPropagation();
    setEditingSearch(name);
  };

  const onDelete = (e, name) => {
    e.stopPropagation();
    dispatch(deleteSavedSearch(name));
    const undoAction = (key) => <Button onClick={() => onUndoDelete(name, key)}>Undo</Button>;
    success(`Search "${name}" deleted!`, { action: undoAction });
  };

  const onUndoDelete = (searchName, key) => {
    const search = savedSearches.find((ss) => ss.name === searchName);
    dispatch(undoDeleteSavedSearch(search));
    closeSnackbar(key);
  };

  return isFiltersV2 && isOnDrawer ? (
    <FormControl fullWidth>
      <InputLabel id="saved-searches">Saved Searches</InputLabel>
      <SavedSearchesSelect
        labelId="saved-searches"
        label="Saved Searches"
        value=""
        onChange={onSavedSearchClick}
        IconComponent={KeyboardArrowDownIcon}
      >
        {savedSearches.map((savedSearch, i) => (
          <MenuItem key={`saved-search-${i}`} id={i} value={i}>
            {savedSearch.name}
          </MenuItem>
        ))}
      </SavedSearchesSelect>
    </FormControl>
  ) : (
    <div>
      <SavedSearchEdit key={editingSearch} savedSearchName={editingSearch} handleClose={() => setEditingSearch(false)} />
      <Button
        aria-controls="saved-searches-menu"
        aria-haspopup="true"
        onClick={handleClick}
        color="default"
        variant="text"
        endIcon={<PolarisIcon icon="down" height={15} />}
        startIcon={<PolarisIcon icon="archive" height={15} />}
      >
        Saved Searches
      </Button>
      <StyledMenuWrapped
        id="saved-searches-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {savedSearches.length ? (
          savedSearches.map((savedSearch, i) => (
            <StyledMenuItemWrapped key={`saved-search-${i}`}>
              <span onClick={onSavedSearchClick} id={i} style={{ width: '100%' }}>
                {savedSearch.name}
              </span>
              <Actions>
                <StyledIconButton size="small" onClick={(e) => onEdit(e, savedSearch.name)} className="editIcon">
                  <PolarisIcon icon="edit_filled" />
                </StyledIconButton>
                <StyledIconButton size="small" onClick={(e) => onDelete(e, savedSearch.name)} className="deleteIcon">
                  <PolarisIcon icon="delete" />
                </StyledIconButton>
              </Actions>
            </StyledMenuItemWrapped>
          ))
        ) : (
          <NoSavedSearches>You have no saved searches.</NoSavedSearches>
        )}
      </StyledMenuWrapped>
    </div>
  );
}

const StyledMenuWrapped = styled(Menu)`
  max-height: 28.5rem;
`;

const StyledMenuItemWrapped = styled(MenuItem)`
  font-family: ${(props) => props.theme.typography.fontFamily};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  color: ${(props) => props.theme.palette.text.primary};
  min-width: 200px;
  display: flex;
  justify-content: space-between;

  &:hover .deleteIcon,
  &:hover .editIcon {
    opacity: 1;
    transition: ease opacity 200ms;
  }
`;

const NoSavedSearches = styled.div`
  padding: ${(props) => props.theme.spacing(1, 2)};
`;

const Actions = styled.div`
  margin-left: ${(props) => props.theme.spacing(2)};
  margin-right: 0;
`;

const StyledIconButton = styled(IconButton)`
  opacity: 0;
  transition: ease opacity 200ms;
  font-size: 16px;

  &.editIcon {
    &:hover {
      color: ${(props) => props.theme.palette.primary.main};
    }
  }

  &.deleteIcon {
    &:hover {
      color: ${(props) => props.theme.palette.error.main};
    }
  }
`;

const SavedSearchesSelect = styled(Select)`
  backgroundcolor: ${(props) => (props.theme.palette.mode === 'dark' ? '#03240' : '#f5f5f5')};
`;
